import { DiscordSupportButton } from '@/components/buttons/discord-support-button';
import { Confetti } from '@/components/confetti';
import { RoundedBadge } from '@/components/rounded-badge';
import { RoundedButton } from '@/components/rounded-button';
import { useAuth } from '@/hooks/useAuth';
import { useDailySpinOffchain } from '@/hooks/useDailySpinOffchain';
import { useDailySpinOnchain } from '@/hooks/useDailySpinOnchain';
import { useRaffle } from '@/hooks/useRaffle';
import { formatNumberToLocale } from '@/utils';
import { Flex, HStack, Heading, Icon, Image, Spacer, Spinner, VStack } from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import { PiQuestion } from 'react-icons/pi';
import { PreviousWinnersList } from './PreviousWinnersList';
import { RaffleFaqModal } from './RaffleFaqModal';
import { RafflePrizesList } from './RafflePrizesList';

export const Raffle = () => {
  const { raffleTicketsBalance, isLoadingDailySpinStats, refetchDailySpinStats } = useDailySpinOnchain();
  const { isAuthenticated } = useAuth();
  const [numTicketsToSubmit, setNumTicketsToSubmit] = useState(0);
  const [isRaffleFaqModalOpen, setIsRaffleFaqModalOpen] = useState(false);
  const { refetch: refetchSpinHistory, isLoading: isLoadingDailySpinOffchain } = useDailySpinOffchain();

  const { rafflePrizes, isLoadingPrizes, refetchRaffle } = useRaffle();

  const refreshRaffleData = useCallback(() => {
    refetchRaffle();
    refetchDailySpinStats?.();
    refetchSpinHistory?.();

    // in case indexing is lagging, refetch spin history again
    setTimeout(() => {
      refetchSpinHistory?.();
    }, 1500);
  }, [refetchRaffle, refetchDailySpinStats, refetchSpinHistory]);

  const rafflePrizesWonByUser = rafflePrizes.filter((prize) => !!prize.isWon && prize.hasEnded && !prize.isActive);
  const ongoingRafflePrizes = rafflePrizes.filter((prize) => !prize.hasEnded && prize.isActive);
  const endedRafflePrizes = rafflePrizes.filter((prize) => prize.hasEnded && !prize.isActive);
  const [showConfetti, setShowConfetti] = useState(false);
  return (
    <VStack alignItems="start" w="full" gap={4}>
      <Confetti showConfetti={showConfetti} setShowConfetti={setShowConfetti} numberOfPieces={2_000} />
      <Flex direction={['column', 'row']} gap={[4, 2]} justifyContent="space-between" w="full">
        <VStack alignItems="start">
          <HStack alignItems="center">
            <Heading size="3xl">Raffle</Heading>
            {isAuthenticated && (
              <RoundedBadge py={1.5} px={4} bg="#F3F1FF">
                <Image src="/assets/daily-spin/raffle-ticket.svg" />
                {isLoadingDailySpinStats ? <Spinner /> : formatNumberToLocale(raffleTicketsBalance)} Raffle Ticket
                {raffleTicketsBalance === 1 ? '' : 's'}
              </RoundedBadge>
            )}
          </HStack>
          <Heading size="lg" fontWeight="500" color="fg.muted">
            Use your tickets in raffles for a chance to win RWA prizes
          </Heading>
        </VStack>
        <Spacer flex={1} />
        <DiscordSupportButton />
        <RoundedButton
          variant="outline"
          onClick={() => setIsRaffleFaqModalOpen(true)}
          bgColor="bg"
          _hover={{
            bgColor: 'bg.muted',
          }}
        >
          <Icon as={PiQuestion} boxSize={4} />
          Raffles FAQ
        </RoundedButton>
        <RaffleFaqModal isOpen={isRaffleFaqModalOpen} onClose={() => setIsRaffleFaqModalOpen(false)} />
      </Flex>
      {rafflePrizesWonByUser && rafflePrizesWonByUser.length > 0 && (
        <>
          <Heading size="2xl">My Winnings</Heading>
          <RafflePrizesList
            isLoading={isLoadingPrizes}
            isLoadingDailySpinOffchain={isLoadingDailySpinOffchain}
            raffleTicketsBalance={raffleTicketsBalance}
            rafflePrizes={rafflePrizesWonByUser}
            numTicketsToSubmit={numTicketsToSubmit}
            setNumTicketsToSubmit={setNumTicketsToSubmit}
            onRefresh={refreshRaffleData}
            placeholder="No ongoing raffles, check back later to see what prizes are in store"
            isAuthenticated={isAuthenticated}
            ctaType="claim"
            showImgQuantity={false}
            setShowConfetti={setShowConfetti}
          />
        </>
      )}
      {ongoingRafflePrizes && ongoingRafflePrizes.length > 0 && <Heading size="2xl">Ongoing</Heading>}
      <RafflePrizesList
        isLoading={isLoadingPrizes}
        isLoadingDailySpinOffchain={isLoadingDailySpinOffchain}
        raffleTicketsBalance={raffleTicketsBalance}
        rafflePrizes={ongoingRafflePrizes}
        numTicketsToSubmit={numTicketsToSubmit}
        setNumTicketsToSubmit={setNumTicketsToSubmit}
        onRefresh={refreshRaffleData}
        placeholder="No ongoing raffles, check back later to see what prizes are in store"
        isAuthenticated={isAuthenticated}
        ctaType="addTickets"
      />
      {endedRafflePrizes && endedRafflePrizes.length > 0 && (
        <>
          <Heading size="2xl">Previous Winners</Heading>
          <PreviousWinnersList
            isLoading={isLoadingPrizes}
            isLoadingDailySpinOffchain={isLoadingDailySpinOffchain}
            raffleTicketsBalance={raffleTicketsBalance}
            rafflePrizes={endedRafflePrizes}
            numTicketsToSubmit={numTicketsToSubmit}
            setNumTicketsToSubmit={setNumTicketsToSubmit}
            onRefresh={refreshRaffleData}
            placeholder=""
            isAuthenticated={isAuthenticated}
          />
        </>
      )}
    </VStack>
  );
};
